import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSnackbar } from 'notistack';

import { PageHeader, LoadingSkeleton } from '../components/common';
import { useNotifications } from '../contexts/NotificationContext';
import UserPushManager from '../components/push/UserPushManager';
import Gate from '../components/access/Gate';
import pushSettingsApi, {
  NotificationPreferences,
  PushDiagnostics,
} from '../services/pushSettingsApi';

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('fa-IR');

const jalali = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const PLATFORM_FA: Record<string, string> = {
  web: 'مرورگر',
  android: 'اندروید',
  ios: 'آیفون',
};

type Verdict = {
  severity: 'success' | 'error' | 'warning' | 'info';
  title: string;
  detail: string;
};

/**
 * Turns the raw diagnostics into one sentence an admin can act on.
 *
 * The checks are ordered by how early they break the chain: a server with
 * Firebase disabled makes every downstream check irrelevant, so it is reported
 * first rather than listing five green ticks and one red cross.
 */
function buildVerdict(
  d: PushDiagnostics | null,
  browserPermission: string,
  configured: boolean,
  supported: boolean,
): Verdict {
  if (!d) {
    return { severity: 'info', title: 'در حال بررسی…', detail: '' };
  }

  if (!d.firebaseEnabled) {
    return {
      severity: 'error',
      title: 'پوش روی سرور خاموش است',
      detail:
        'سرور Firebase را بالا نیاورده — یعنی `FIREBASE_ENABLED` یا کلیدهایش در محیط سرور تنظیم نشده‌اند. ' +
        'تا این درست نشود هیچ پوشی برای هیچ‌کس (ادمین، فروشنده، خریدار، اپ موبایل) ارسال نمی‌شود، ' +
        'هرچند اعلان درون‌برنامه و ایمیل سر جایشان کار می‌کنند.',
    };
  }

  if (!supported) {
    return {
      severity: 'warning',
      title: 'این مرورگر پوش را پشتیبانی نمی‌کند',
      detail: 'مرورگر دیگری امتحان کنید. روی iOS باید سایت را به صفحهٔ اصلی اضافه کنید.',
    };
  }

  if (!configured) {
    return {
      severity: 'error',
      title: 'کانفیگ Firebase در این بیلد نیست',
      detail: 'متغیرهای `REACT_APP_FIREBASE_*` موقع build ست نشده‌اند؛ پنل باید دوباره build شود.',
    };
  }

  if (browserPermission === 'denied') {
    return {
      severity: 'error',
      title: 'اجازهٔ اعلان در مرورگر رد شده است',
      detail:
        'از نوار آدرس، قفل کنار دامنه → Notifications → Allow. مرورگر بعد از رد کردن، ' +
        'دیگر خودش نمی‌پرسد.',
    };
  }

  if (browserPermission !== 'granted') {
    return {
      severity: 'warning',
      title: 'هنوز اجازهٔ اعلان نداده‌اید',
      detail: 'دکمهٔ «فعال‌سازی اعلان‌ها» را بزنید تا مرورگر اجازه بگیرد و دستگاه ثبت شود.',
    };
  }

  if (d.devices === 0) {
    return {
      severity: 'warning',
      title: 'اجازه داده شده ولی هیچ دستگاهی ثبت نشده',
      detail:
        'توکن به سرور نرسیده است. یک بار صفحه را رفرش کنید؛ اگر باز هم صفر ماند، ' +
        'Service Worker مسدود شده (حالت ناشناس یا افزونهٔ مسدودکننده).',
    };
  }

  if (d.preferences?.pushEnabled === false) {
    return {
      severity: 'warning',
      title: 'پوش را در همین صفحه خاموش کرده‌اید',
      detail: 'کلید اصلی «دریافت پوش‌نوتیفیکیشن» را روشن کنید.',
    };
  }

  const quiet = d.preferences?.quietHours;
  if (quiet?.enabled) {
    return {
      severity: 'info',
      title: 'همه‌چیز درست است — ولی «ساعات سکوت» فعال است',
      detail: `بین ساعت ${fmt(quiet.startHour)} تا ${fmt(quiet.endHour)} پوش ارسال نمی‌شود (اعلان درون‌برنامه ساخته می‌شود).`,
    };
  }

  return {
    severity: 'success',
    title: 'پوش فعال است',
    detail: `${fmt(d.devices)} دستگاه ثبت شده روی پروژهٔ ${d.firebaseProjectId || '—'}. با دکمهٔ «ارسال تستی» امتحان کنید.`,
  };
}

const PushSettingsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { pushPermission, pushSupported, pushConfigured, pushBusy, enablePush } = useNotifications();

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [diag, setDiag] = useState<PushDiagnostics | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, d] = await Promise.all([
      pushSettingsApi.getPreferences(),
      pushSettingsApi.getDiagnostics(),
    ]);
    if (p.success && p.data) setPrefs(p.data);
    if (d.success && d.data) setDiag(d.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Granting permission registers a device, which changes the diagnosis.
  useEffect(() => {
    if (pushPermission === 'granted' && diag && diag.devices === 0) {
      const t = setTimeout(() => void load(), 1500);
      return () => clearTimeout(t);
    }
  }, [pushPermission, diag, load]);

  const verdict = useMemo(
    () => buildVerdict(diag, pushPermission, pushConfigured, pushSupported),
    [diag, pushPermission, pushConfigured, pushSupported],
  );

  const save = async (patch: {
    pushEnabled?: boolean;
    mutedCategories?: string[];
    quietHours?: NotificationPreferences['quietHours'];
  }) => {
    setSaving(true);
    const res = await pushSettingsApi.updatePreferences(patch);
    setSaving(false);
    if (res.success && res.data) {
      // The PUT returns preferences without the catalogue, so keep ours.
      setPrefs((prev) => (prev ? { ...prev, ...res.data } : prev));
      void pushSettingsApi.getDiagnostics().then((d) => {
        if (d.success && d.data) setDiag(d.data);
      });
      enqueueSnackbar(res.message || 'ذخیره شد', { variant: 'success' });
    } else {
      enqueueSnackbar(res.message || 'خطا در ذخیره', { variant: 'error' });
    }
  };

  const toggleCategory = (id: string, muted: boolean) => {
    if (!prefs) return;
    const next = muted
      ? Array.from(new Set([...prefs.mutedCategories, id]))
      : prefs.mutedCategories.filter((c) => c !== id);
    setPrefs({ ...prefs, mutedCategories: next });
    void save({ mutedCategories: next });
  };

  const handleTest = async () => {
    setTesting(true);
    const res = await pushSettingsApi.sendTestToSelf();
    setTesting(false);
    enqueueSnackbar(res.message || (res.success ? 'ارسال شد' : 'خطا'), {
      variant: res.success ? 'success' : 'error',
    });
  };

  const VerdictIcon =
    verdict.severity === 'success'
      ? CheckCircleIcon
      : verdict.severity === 'error'
        ? ErrorIcon
        : WarningAmberIcon;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <PageHeader
        title="پوش‌نوتیفیکیشن"
        subtitle="وضعیت دریافت اعلان روی این مرورگر و تنظیمات دسته‌ها"
      >
        <Stack direction="row" spacing={1}>
          <Tooltip title="بررسی مجدد">
            <IconButton onClick={() => void load()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={16} /> : <ScienceIcon />}
            onClick={() => void handleTest()}
            disabled={testing}
          >
            ارسال تستی
          </Button>
        </Stack>
      </PageHeader>

      {loading ? (
        <LoadingSkeleton variant="detail" />
      ) : (
        <Stack spacing={2}>
          {/* ── the one-sentence answer ─────────────────────────────── */}
          <Alert
            severity={verdict.severity}
            icon={<VerdictIcon />}
            action={
              pushPermission !== 'granted' && pushSupported && pushConfigured ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={pushBusy}
                  onClick={() => void enablePush()}
                >
                  فعال‌سازی اعلان‌ها
                </Button>
              ) : undefined
            }
          >
            <AlertTitle sx={{ fontWeight: 800 }}>{verdict.title}</AlertTitle>
            {verdict.detail}
          </Alert>

          {/* ── the chain, so it is obvious which link is broken ────── */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
              زنجیرهٔ تحویل
            </Typography>
            <Grid container spacing={1.5}>
              {[
                {
                  label: 'سرور (Firebase)',
                  ok: !!diag?.firebaseEnabled,
                  value: diag?.firebaseProjectId || 'تنظیم نشده',
                },
                {
                  label: 'کانفیگ این پنل',
                  ok: pushConfigured,
                  value: pushConfigured ? 'موجود' : 'ناقص',
                },
                {
                  label: 'اجازهٔ مرورگر',
                  ok: pushPermission === 'granted',
                  value:
                    pushPermission === 'granted'
                      ? 'داده شده'
                      : pushPermission === 'denied'
                        ? 'رد شده'
                        : 'پرسیده نشده',
                },
                {
                  label: 'دستگاه‌های ثبت‌شده',
                  ok: (diag?.devices ?? 0) > 0,
                  value: fmt(diag?.devices),
                },
              ].map((row) => (
                <Grid item xs={6} md={3} key={row.label}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: row.ok ? theme.palette.success.main : theme.palette.error.main,
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {row.ok ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : (
                        <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {row.label}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                      {row.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* ── master switch + quiet hours ─────────────────────────── */}
          {prefs && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={800}>
                    دریافت پوش‌نوتیفیکیشن
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    خاموش‌کردن این کلید فقط پوش را می‌بندد؛ اعلان‌ها همچنان در زنگوله می‌آیند.
                  </Typography>
                </Box>
                <Switch
                  checked={prefs.pushEnabled}
                  disabled={saving}
                  onChange={(e) => {
                    setPrefs({ ...prefs, pushEnabled: e.target.checked });
                    void save({ pushEnabled: e.target.checked });
                  }}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={800}>
                    ساعات سکوت
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    در این بازه پوش ارسال نمی‌شود (به وقت تهران). دسته‌های مالی و امنیتی مستثنا هستند.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 90 }} disabled={!prefs.quietHours.enabled}>
                    <InputLabel>از</InputLabel>
                    <Select
                      label="از"
                      value={prefs.quietHours.startHour}
                      onChange={(e) => {
                        const q = { ...prefs.quietHours, startHour: Number(e.target.value) };
                        setPrefs({ ...prefs, quietHours: q });
                        void save({ quietHours: q });
                      }}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <MenuItem key={h} value={h}>{fmt(h)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 90 }} disabled={!prefs.quietHours.enabled}>
                    <InputLabel>تا</InputLabel>
                    <Select
                      label="تا"
                      value={prefs.quietHours.endHour}
                      onChange={(e) => {
                        const q = { ...prefs.quietHours, endHour: Number(e.target.value) };
                        setPrefs({ ...prefs, quietHours: q });
                        void save({ quietHours: q });
                      }}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <MenuItem key={h} value={h}>{fmt(h)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Switch
                    checked={prefs.quietHours.enabled}
                    disabled={saving}
                    onChange={(e) => {
                      const q = { ...prefs.quietHours, enabled: e.target.checked };
                      setPrefs({ ...prefs, quietHours: q });
                      void save({ quietHours: q });
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* ── per-category mute ───────────────────────────────────── */}
          {prefs && prefs.categories?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
                دسته‌ها
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                هر دسته را جداگانه می‌توانید بی‌صدا کنید. دسته‌های قفل‌شده همیشه ارسال می‌شوند.
              </Typography>
              <Stack divider={<Divider />}>
                {prefs.categories.map((c) => {
                  const muted = prefs.mutedCategories.includes(c.id);
                  return (
                    <Stack
                      key={c.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                      sx={{ py: 1.25 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="body2" fontWeight={700}>
                            {c.label}
                          </Typography>
                          {!c.mutable && (
                            <Chip size="small" label="همیشه فعال" variant="outlined" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {c.description}
                        </Typography>
                      </Box>
                      <Switch
                        checked={!muted}
                        disabled={!c.mutable || saving || !prefs.pushEnabled}
                        onChange={(e) => toggleCategory(c.id, !e.target.checked)}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          )}

          {/* ── registered devices ──────────────────────────────────── */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <DevicesIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={800}>
                دستگاه‌های این حساب
              </Typography>
            </Stack>
            {!prefs?.devices?.length ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <NotificationsOffIcon fontSize="small" color="disabled" />
                <Typography variant="body2" color="text.secondary">
                  هیچ دستگاهی ثبت نشده است.
                </Typography>
              </Stack>
            ) : (
              <Stack divider={<Divider />}>
                {prefs.devices.map((d, i) => (
                  <Stack
                    key={`${d.platform}-${i}`}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ py: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <NotificationsActiveIcon fontSize="small" color="success" />
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {PLATFORM_FA[d.platform] || d.platform}
                          {d.deviceModel ? ` — ${d.deviceModel}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          آخرین فعالیت: {jalali(d.lastSeenAt)}
                          {d.appVersion ? ` • نسخه ${d.appVersion}` : ''}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>

          {/* ابزار پشتیبانی: مدیریت پوش یک کاربر مشخص (با پرمیشن جدا) */}
          <Gate permission="admin_regular_user_read_single">
            <UserPushManager />
          </Gate>
        </Stack>
      )}
    </Box>
  );
};

export default PushSettingsPage;
